import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { X, Upload, CopyPlus, FileText } from "lucide-react";
import type {
  DuplicateDocumentMatch,
  ConflictResolution,
} from "@/hooks/use-upload-documents";

interface DuplicateConflictModalProps {
  open: boolean;
  conflicts: DuplicateDocumentMatch[];
  nextAvailableNames?: Record<string, string>;
  onResolveAll: (resolutions: Map<string, ConflictResolution>) => void;
  onCancel: () => void;
}

export function DuplicateConflictModal({
  open,
  conflicts,
  nextAvailableNames,
  onResolveAll,
  onCancel,
}: DuplicateConflictModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolutions, setResolutions] = useState<
    Map<string, ConflictResolution>
  >(new Map());

  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setResolutions(new Map());
    }
  }, [open]);

  if (conflicts.length === 0) return null;

  const conflict = conflicts[currentIndex];
  if (!conflict) return null;

  const advance = (next: Map<string, ConflictResolution>) => {
    if (currentIndex < conflicts.length - 1) {
      setResolutions(next);
      setCurrentIndex((i) => i + 1);
    } else {
      onResolveAll(next);
    }
  };

  const resolve = (resolution: ConflictResolution) => {
    const next = new Map(resolutions);
    next.set(conflict.fileName, resolution);
    advance(next);
  };

  const handleNewVersion = () => {
    resolve({
      type: "new-version",
      existingDocumentId: conflict.existingDocumentId,
    });
  };

  const handleKeepBoth = () => {
    const newName =
      nextAvailableNames?.[conflict.fileName] ??
      (() => {
        const dotIndex = conflict.fileName.lastIndexOf(".");
        return dotIndex > 0
          ? `${conflict.fileName.slice(0, dotIndex)} (2)${conflict.fileName.slice(dotIndex)}`
          : `${conflict.fileName} (2)`;
      })();
    resolve({ type: "rename", newFileName: newName });
  };

  const suggestedName = nextAvailableNames?.[conflict.fileName] ?? null;

  const formatDate = (ts: number) =>
    new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(ts));

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </Dialog.Overlay>

            <Dialog.Content
              asChild
              onPointerDownOutside={() => onCancel()}
              onEscapeKeyDown={() => onCancel()}
            >
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={(e) => {
                  // Dismiss when clicking the backdrop area (outside the card)
                  if (e.target === e.currentTarget) onCancel();
                }}
              >
                <motion.div
                  className="w-full max-w-[520px] rounded-xl bg-card shadow-xl mx-4"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 pt-5 pb-3">
                    <div>
                      <Dialog.Title className="text-base font-semibold text-primary">
                        Duplicate File Detected
                      </Dialog.Title>
                      <p className="text-xs text-muted mt-0.5">
                        Conflict {currentIndex + 1} of {conflicts.length}
                      </p>
                    </div>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-muted hover:text-primary hover:bg-card-hover transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </Dialog.Close>
                  </div>

                  <div className="h-px bg-border" />

                  {/* Body */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentIndex}
                      className="px-5 py-4 space-y-4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      {/* Uploading file */}
                      <div>
                        <p className="text-xs text-muted mb-1.5">
                          You're uploading:
                        </p>
                        <div className="flex items-center gap-2">
                          <FileText
                            size={16}
                            className="text-accent shrink-0"
                          />
                          <span className="text-sm font-medium text-primary truncate">
                            {conflict.fileName}
                          </span>
                        </div>
                      </div>

                      {/* Existing document card */}
                      <div>
                        <p className="text-xs text-muted mb-1.5">
                          A document with this name already exists:
                        </p>
                        <div className="rounded-lg border border-border px-3.5 py-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <FileText
                              size={14}
                              className="text-muted shrink-0"
                            />
                            <span className="text-sm font-medium text-primary truncate">
                              {conflict.fileName}
                            </span>
                          </div>
                          <p className="text-xs text-muted">
                            Uploaded by {conflict.uploadedByName} &middot;{" "}
                            {formatDate(conflict.uploadedAt)}
                          </p>
                          {conflict.summaryPreview ? (
                            <p className="text-xs text-secondary italic leading-relaxed">
                              &ldquo;{conflict.summaryPreview}
                              {conflict.summaryPreview.length >= 150
                                ? "..."
                                : ""}
                              &rdquo;
                            </p>
                          ) : (
                            <p className="text-xs text-muted italic">
                              No summary available
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Resolution options */}
                      <div>
                        <p className="text-xs text-muted mb-2">
                          How would you like to proceed?
                        </p>

                        <div className="space-y-2">
                          {/* New version */}
                          <button
                            type="button"
                            onClick={handleNewVersion}
                            className="w-full text-left rounded-lg border border-border px-3.5 py-2.5 hover:border-accent/40 hover:bg-accent/5 transition-colors group"
                          >
                            <div className="flex items-center gap-2.5">
                              <Upload
                                size={15}
                                className="text-muted group-hover:text-accent shrink-0"
                              />
                              <div>
                                <p className="text-sm font-medium text-primary">
                                  Upload as new version
                                </p>
                                <p className="text-xs text-muted">
                                  Replaces the existing document
                                </p>
                              </div>
                            </div>
                          </button>

                          {/* Upload anyway */}
                          <button
                            type="button"
                            onClick={handleKeepBoth}
                            className="w-full text-left rounded-lg border border-border px-3.5 py-2.5 hover:border-accent/40 hover:bg-accent/5 transition-colors group"
                          >
                            <div className="flex items-center gap-2.5">
                              <CopyPlus
                                size={15}
                                className="text-muted group-hover:text-accent shrink-0"
                              />
                              <div>
                                <p className="text-sm font-medium text-primary">
                                  Upload anyway
                                </p>
                                <p className="text-xs text-muted">
                                  {suggestedName
                                    ? `Saves as "${suggestedName}"`
                                    : "Creates a separate document with a number appended"}
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
